import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Typography,
} from "antd";
import LinearProgress from "@material-ui/core/LinearProgress";
import { ReloadOutlined } from "@ant-design/icons";
import { format } from "date-fns";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

const toDate = (value) => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  const date = toDate(value);

  return date ? format(date, "dd/MM/yyyy HH:mm") : "-";
};

const getCouponLabel = (coupon) =>
  `${coupon.code}${coupon.name ? ` · ${coupon.name}` : ""}`;

const getCardCouponCode = (card) => (card.couponCode || "").trim().toUpperCase();

const getCardPlan = (card) => card.plan || "gratis";

const getEmailDomain = (email = "") => {
  const parts = email.trim().toLowerCase().split("@");

  return parts.length === 2 && parts[1] ? parts[1] : "";
};

const StatisticsPage = () => {
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  const { getTokenSilently } = useAuth0();
  const [cards, setCards] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [couponFilter, setCouponFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [emailDomainFilter, setEmailDomainFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");

  const apiRequest = useCallback(
    async (path) => {
      const token = await getTokenSilently();
      const response = await fetch(`${config.baseUrl[env]}${path}`, {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Impossibile caricare le statistiche");
      }

      return data;
    },
    [env, getTokenSilently],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [cardsResponse, couponsResponse] = await Promise.all([
        apiRequest(`/admin?${new URLSearchParams({ size: 5000 })}`),
        apiRequest("/admin/coupons"),
      ]);

      setCards(cardsResponse.data || []);
      setCoupons(couponsResponse.data || []);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const couponOptions = useMemo(() => {
    const couponCodesFromCards = cards
      .map(getCardCouponCode)
      .filter(Boolean)
      .filter((value, index, values) => values.indexOf(value) === index);
    const knownCouponCodes = new Set(coupons.map((coupon) => coupon.code));
    const couponRows = coupons.map((coupon) => ({
      code: coupon.code,
      label: getCouponLabel(coupon),
    }));
    const unknownCouponRows = couponCodesFromCards
      .filter((code) => !knownCouponCodes.has(code))
      .map((code) => ({
        code,
        label: `${code} · non presente in lista coupon`,
      }));

    return [...couponRows, ...unknownCouponRows].sort((a, b) =>
      a.code.localeCompare(b.code),
    );
  }, [cards, coupons]);

  const emailDomainOptions = useMemo(() => {
    const domains = cards.map((card) => getEmailDomain(card.email)).filter(Boolean);

    return [...new Set(domains)].sort((a, b) => a.localeCompare(b));
  }, [cards]);

  const companyOptions = useMemo(() => {
    const companies = cards
      .map((card) => (card.companyName || "").trim())
      .filter(Boolean);

    return [...new Set(companies)].sort((a, b) => a.localeCompare(b));
  }, [cards]);

  const filteredCards = useMemo(() => {
    const startDate = dateRange?.[0]?.clone().startOf("day").valueOf();
    const endDate = dateRange?.[1]?.clone().endOf("day").valueOf();

    return cards.filter((card) => {
      const createdAt = toDate(card.createdAt);
      const couponCode = getCardCouponCode(card);

      if (startDate && (!createdAt || createdAt.getTime() < startDate)) {
        return false;
      }

      if (endDate && (!createdAt || createdAt.getTime() > endDate)) {
        return false;
      }

      if (couponFilter === "none" && couponCode) {
        return false;
      }

      if (
        couponFilter !== "all" &&
        couponFilter !== "none" &&
        couponCode !== couponFilter
      ) {
        return false;
      }

      if (statusFilter === "active" && card.isDisabled) {
        return false;
      }

      if (statusFilter === "disabled" && !card.isDisabled) {
        return false;
      }

      if (planFilter !== "all" && getCardPlan(card) !== planFilter) {
        return false;
      }

      if (
        emailDomainFilter !== "all" &&
        getEmailDomain(card.email) !== emailDomainFilter
      ) {
        return false;
      }

      if (
        companyFilter !== "all" &&
        (card.companyName || "").trim() !== companyFilter
      ) {
        return false;
      }

      return true;
    });
  }, [
    cards,
    companyFilter,
    couponFilter,
    dateRange,
    emailDomainFilter,
    planFilter,
    statusFilter,
  ]);

  const summary = useMemo(() => {
    const active = filteredCards.filter((card) => !card.isDisabled).length;
    const disabled = filteredCards.length - active;
    const plus = filteredCards.filter((card) => getCardPlan(card) === "plus")
      .length;
    const free = filteredCards.length - plus;
    const withCoupon = filteredCards.filter((card) =>
      Boolean(getCardCouponCode(card)),
    ).length;

    return {
      total: filteredCards.length,
      active,
      disabled,
      plus,
      free,
      withCoupon,
    };
  }, [filteredCards]);

  const couponStats = useMemo(() => {
    const couponMap = coupons.reduce((acc, coupon) => {
      acc[coupon.code] = coupon;
      return acc;
    }, {});
    const stats = filteredCards.reduce((acc, card) => {
      const code = getCardCouponCode(card) || "NESSUN_COUPON";

      if (!acc[code]) {
        acc[code] = {
          code,
          name: couponMap[code]?.name || "",
          total: 0,
          active: 0,
          plus: 0,
          firstCreatedAt: null,
          lastCreatedAt: null,
        };
      }

      const row = acc[code];
      const createdAt = toDate(card.createdAt);
      row.total += 1;
      row.active += card.isDisabled ? 0 : 1;
      row.plus += getCardPlan(card) === "plus" ? 1 : 0;

      if (createdAt) {
        row.firstCreatedAt =
          !row.firstCreatedAt || createdAt < row.firstCreatedAt
            ? createdAt
            : row.firstCreatedAt;
        row.lastCreatedAt =
          !row.lastCreatedAt || createdAt > row.lastCreatedAt
            ? createdAt
            : row.lastCreatedAt;
      }

      return acc;
    }, {});

    return Object.values(stats).sort((a, b) => b.total - a.total);
  }, [coupons, filteredCards]);

  const recentCards = useMemo(
    () =>
      [...filteredCards]
        .sort((a, b) => {
          const dateA = toDate(a.createdAt)?.getTime() || 0;
          const dateB = toDate(b.createdAt)?.getTime() || 0;
          return dateB - dateA;
        })
        .slice(0, 10),
    [filteredCards],
  );

  const resetFilters = () => {
    setDateRange(null);
    setCouponFilter("all");
    setStatusFilter("all");
    setPlanFilter("all");
    setEmailDomainFilter("all");
    setCompanyFilter("all");
  };

  return (
    <div style={{ margin: "16px 16px" }}>
      <Title level={3}>Statistiche Cards</Title>
      {loading && <LinearProgress />}
      {error && (
        <Alert
          type="error"
          showIcon
          message="Statistiche non disponibili"
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card style={{ marginBottom: 16 }}>
        <Space size="middle" wrap>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            format="DD/MM/YYYY"
            allowClear
          />
          <Select
            value={couponFilter}
            onChange={setCouponFilter}
            style={{ minWidth: 260 }}
          >
            <Option value="all">Tutti i coupon</Option>
            <Option value="none">Senza coupon</Option>
            {couponOptions.map((coupon) => (
              <Option key={coupon.code} value={coupon.code}>
                {coupon.label}
              </Option>
            ))}
          </Select>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 160 }}
          >
            <Option value="all">Tutti gli stati</Option>
            <Option value="active">Attive</Option>
            <Option value="disabled">Disattivate</Option>
          </Select>
          <Select
            value={planFilter}
            onChange={setPlanFilter}
            style={{ width: 140 }}
          >
            <Option value="all">Tutti i piani</Option>
            <Option value="gratis">Gratis</Option>
            <Option value="plus">Plus</Option>
          </Select>
          <Select
            value={emailDomainFilter}
            onChange={setEmailDomainFilter}
            showSearch
            optionFilterProp="children"
            style={{ minWidth: 220 }}
          >
            <Option value="all">Tutti i domini email</Option>
            {emailDomainOptions.map((domain) => (
              <Option key={domain} value={domain}>
                {domain}
              </Option>
            ))}
          </Select>
          <Select
            value={companyFilter}
            onChange={setCompanyFilter}
            showSearch
            optionFilterProp="children"
            style={{ minWidth: 260 }}
          >
            <Option value="all">Tutte le imprese</Option>
            {companyOptions.map((companyName) => (
              <Option key={companyName} value={companyName}>
                {companyName}
              </Option>
            ))}
          </Select>
          <Button onClick={resetFilters}>Reset filtri</Button>
          <Button icon={<ReloadOutlined />} onClick={fetchData}>
            Aggiorna
          </Button>
        </Space>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="Cards filtrate" value={summary.total} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="Attive" value={summary.active} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="Disattivate" value={summary.disabled} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="Plus" value={summary.plus} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="Gratis" value={summary.free} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="Con coupon" value={summary.withCoupon} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={14}>
          <Card title="Distribuzione per coupon">
            {couponStats.length ? (
              <Table
                size="small"
                rowKey="code"
                dataSource={couponStats}
                pagination={false}
                columns={[
                  {
                    title: "Coupon",
                    dataIndex: "code",
                    render: (code, record) =>
                      code === "NESSUN_COUPON" ? (
                        <Text type="secondary">Senza coupon</Text>
                      ) : (
                        <span>
                          <strong>{code}</strong>
                          {record.name ? ` · ${record.name}` : ""}
                        </span>
                      ),
                  },
                  {
                    title: "Totale",
                    dataIndex: "total",
                    sorter: (a, b) => a.total - b.total,
                    defaultSortOrder: "descend",
                  },
                  {
                    title: "Attive",
                    dataIndex: "active",
                    sorter: (a, b) => a.active - b.active,
                  },
                  {
                    title: "Plus",
                    dataIndex: "plus",
                    sorter: (a, b) => a.plus - b.plus,
                  },
                  {
                    title: "Prima creazione",
                    dataIndex: "firstCreatedAt",
                    render: formatDate,
                  },
                  {
                    title: "Ultima creazione",
                    dataIndex: "lastCreatedAt",
                    render: formatDate,
                  },
                ]}
              />
            ) : (
              <Empty description="Nessuna card nei filtri selezionati" />
            )}
          </Card>
        </Col>
        <Col xs={24} xl={10}>
          <Card title="Ultime card create">
            {recentCards.length ? (
              <Table
                size="small"
                rowKey="slug"
                dataSource={recentCards}
                pagination={false}
                columns={[
                  {
                    title: "Card",
                    render: (_, record) =>
                      `${record.firstName || ""} ${record.lastName || ""}`.trim() ||
                      record.companyName ||
                      record.slug,
                  },
                  {
                    title: "Coupon",
                    render: (_, record) => getCardCouponCode(record) || "-",
                  },
                  {
                    title: "Stato",
                    render: (_, record) => (
                      <Badge
                        color={record.isDisabled ? "#d46b08" : "#389e0d"}
                        text={record.isDisabled ? "Disattivata" : "Attiva"}
                      />
                    ),
                  },
                  {
                    title: "Creata",
                    dataIndex: "createdAt",
                    render: formatDate,
                  },
                ]}
              />
            ) : (
              <Empty description="Nessuna card recente" />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default StatisticsPage;
